import { Button, Grid } from '@mui/material';
import * as yup from 'yup';
import { Box } from '@mui/system';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { multipleInvoiceEmailSender } from 'src/shared/services/invoiceService';
import { InvoiceDeliveryMethodIntegerEnum } from 'src/shared/models/enums/invoiceDeliveryMethodInteger.enum';

type InvoiceMailSenderModalProps = {
  modalOpen: boolean;
  invoiceIds: number[];
  onSave: () => void;
  onClose: () => void;
};

type FormValues = {
  subject: string;
  body: string;
  invoiceIds: number[];
  deliveryMethod: InvoiceDeliveryMethodIntegerEnum;
};

export const InvoiceMailSenderModal = (props: InvoiceMailSenderModalProps) => {
  const { modalOpen, invoiceIds, onSave, onClose } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);

  // Ta bort HTML-taggar från editor
  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const minimumCharRequired = 5;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });

  const InvoiceMailSenderSchema: yup.Schema<FormValues> = yup.object().shape({
    subject: yup.string().min(minimumCharRequired, minCharRequiredMessage).required(),
    body: yup
      .string()
      .test('min-length', minCharRequiredMessage, (value) => {
        const sanitizedValue = stripHtml(value || '');
        return sanitizedValue.length >= minimumCharRequired;
      })
      .required(),
    invoiceIds: yup.array().of(yup.number().required()).required(),
    deliveryMethod: yup.number().required(),
  });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(InvoiceMailSenderSchema),
    mode: 'all',
    defaultValues: {
      subject: '',
      body: '',
      invoiceIds: invoiceIds || [],
      deliveryMethod: InvoiceDeliveryMethodIntegerEnum.mail,
    },
  });

  const {
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    try {
      await multipleInvoiceEmailSender(data);
      if (isMounted()) {
        if (invoiceIds.length === 1) {
          toast.success(<>{t('invoice:createMemberInvoice.invoiceSend')}</>);
        } else {
          toast.success(<>{t('invoice:createMemberInvoice.invoicesSend')}</>);
        }

        onClose();
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      disableBackdropClosing
      dialogTitle={t('invoice:createMemberInvoice.modalHeaderSendEmail')}
      onClose={onClose}
      onSave={onSave}
      showDialogActions
      dialogActions={
        <Box
          sx={{
            pr: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button type='submit' form='invoiceForm' disabled={loading || Object.keys(errors).length > 0} disableElevation variant='contained'>
            {t('invoice:createMemberInvoice.sendInvoices')}
          </Button>
        </Box>
      }
      dialogContent={
        <Box
          sx={{
            position: 'relative',
            minHeight: '22rem',
          }}
        >
          <LoadingOverlay loading={loading} />
          <FormProvider {...formMethods}>
            <form id='invoiceForm' onSubmit={handleSubmit(onSubmit)}>
              <GridWrapper>
                <Grid item xs={12} mt={1.5}>
                  <Box sx={{ p: 2 }}>
                    <GridWrapper>
                      <Grid item xs={12} mt={1.5}>
                        <ControlledTextField name='subject' label={t('invoice:createInvoice.invoiceRow.heading')} />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextEditor rows={30} name='body' />
                      </Grid>
                    </GridWrapper>
                  </Box>
                </Grid>
              </GridWrapper>
            </form>
          </FormProvider>
        </Box>
      }
    />
  );
};
